import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { ParameterDto, ParameterEditDto } from 'src/store/parameters/parameter';
import { alertSuccess, alertError } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum ParameterTypes {
  FetchAll = 'parameters/FetchAll',
  Add = 'parameters/Add',
  Update = 'parameters/Update',
  Delete = 'parameters/Delete',
  Failure = 'parameters/Failure'
}

export type ParameterAction =
  | { type: ParameterTypes.FetchAll; parameters: ParameterDto[] }
  | { type: ParameterTypes.Add; parameter: ParameterDto }
  | { type: ParameterTypes.Update; parameter: ParameterDto }
  | { type: ParameterTypes.Delete; parameter: ParameterDto }
  | { type: ParameterTypes.Failure; errors: any };

export const actionCreators = {
  fetchParameters: (parameters: ParameterDto[]): ParameterAction => ({
    type: ParameterTypes.FetchAll,
    parameters
  }),
  addParameter: (parameter: ParameterDto): ParameterAction => ({
    type: ParameterTypes.Add,
    parameter
  }),
  updateParameter: (parameter: ParameterDto): ParameterAction => ({
    type: ParameterTypes.Update,
    parameter
  }),
  deleteParameter: (parameter: ParameterDto): ParameterAction => ({
    type: ParameterTypes.Delete,
    parameter
  }),
  parameterRequestFailure: (errors: any): ParameterAction => ({
    type: ParameterTypes.Failure,
    errors
  })
};

export const fetchParameters = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ParameterTypes.FetchAll));
    const { ok, data, error } = await getRequest<ParameterDto[]>('/parameters');
    dispatch(requestEnd(ParameterTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchParameters(data))
      : dispatch(actionCreators.parameterRequestFailure(error));
  };
};

export const createParameter = (parameter: ParameterEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/parameters', parameter);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('parameter.created')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.parameterRequestFailure(error));
  };
};

export const editParameter = (id: number, parameter: ParameterEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/parameters/${id}`, parameter);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('parameter.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.parameterRequestFailure(error));
  };
};

export const deleteParameter = (id: number, parameter: ParameterDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/parameters/${id}`, parameter);

    if (ok) {
      dispatch(alertSuccess(i18n.t('parameter.deleted')));
      return dispatch(actionCreators.deleteParameter(parameter));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.parameterRequestFailure(error));
  };
};
